<template>
  <section class="device-info-panel">
    <button
      class="update-box"
      type="button"
      :disabled="refreshing"
      @click="$emit('refresh')"
    >
      <span class="update-icon"></span>
      <span>{{ refreshing ? `${refreshCountdown}s` : "更新数据" }}</span>
    </button>

    <transition name="refresh-loading-fade">
      <div v-if="refreshing" class="refresh-loading" role="status" aria-live="polite">
        <div class="refresh-loading__spinner">
          <span>{{ refreshCountdown > 0 ? `${refreshCountdown}s` : "..." }}</span>
        </div>
        <p>{{ refreshCountdown > 0 ? "等待设备更新数据" : "正在加载最新数据" }}</p>
      </div>
    </transition>

    <div class="device-image"></div>

    <dl class="device-info-list">
      <div class="device-info-row">
        <dt>序号</dt>
        <dd>{{ deviceInfo.id }}</dd>
      </div>
      <div class="device-info-row">
        <dt>设备型号</dt>
        <dd>{{ deviceInfo.modelName }}</dd>
      </div>
      <div class="device-info-row">
        <dt>设备状态</dt>
        <dd :class="deviceInfo.status === 'online' ? 'is-online' : 'is-offline'">
          {{ deviceInfo.status === "online" ? "在线" : "离线" }}
        </dd>
      </div>
      <div class="device-info-row">
        <dt>设备序列号</dt>
        <dd>{{ deviceInfo.seriesNumber }}</dd>
      </div>
      <div class="device-info-row">
        <dt>数据更新时间</dt>
        <dd>{{ deviceInfo.updateTime }}</dd>
      </div>
      <div v-if="hostNames.length" class="device-info-row device-name-row">
        <dt>设备位置</dt>
        <dd :title="hostName">{{ hostName }}</dd>
        <div v-if="hasMultipleHostNames" class="host-name-switcher">
          <i
            class="el-icon-caret-top host-name-switcher__icon"
            role="button"
            tabindex="0"
            aria-label="切换到上一个设备位置"
            @click="showPreviousHostName"
            @keydown.enter="showPreviousHostName"
            @keydown.space.prevent="showPreviousHostName"
          ></i>
          <i
            class="el-icon-caret-bottom host-name-switcher__icon"
            role="button"
            tabindex="0"
            aria-label="切换到下一个设备位置"
            @click="showNextHostName"
            @keydown.enter="showNextHostName"
            @keydown.space.prevent="showNextHostName"
          ></i>
        </div>
      </div>
    </dl>
  </section>
</template>

<script>
import { parseDeviceInfo } from "../../utils/protocol/deviceInfo";

export default {
  name: "DeviceInfoPanel",
  props: {
    protocol: {
      type: Object,
      default: () => ({})
    },
    refreshing: {
      type: Boolean,
      default: false
    },
    refreshCountdown: {
      type: Number,
      default: 30
    }
  },
  data() {
    return {
      hostNames: [],
      hostNameIndex: 0
    };
  },
  computed: {
    deviceInfo() {
      return parseDeviceInfo(this.protocol);
    },
    seriesNumber() {
      return this.deviceInfo.seriesNumber;
    },
    hostName() {
      return this.hostNames[this.hostNameIndex] || "--";
    },
    hasMultipleHostNames() {
      return this.hostNames.length > 1;
    }
  },
  watch: {
    seriesNumber: {
      immediate: true,
      handler(serial) {
        this.loadHostName(serial);
      }
    }
  },
  methods: {
    loadHostName(serial) {
      this.hostNames = [];
      this.hostNameIndex = 0;

      if (!serial || serial === "--") {
        return;
      }

      Promise.all([
        this.$api.equipment.getHostNameByZys(serial).catch(() => null),
        this.$api.equipment.getHostNameByLyj(serial).catch(() => null)
      ])
        .then(results => {
          if (serial !== this.seriesNumber) {
            return;
          }

          this.hostNames = results
            .map(result => result && result.data)
            .filter(hostName => hostName && hostName !== "null");
          this.hostNameIndex = 0;
        })
        .catch(() => {
          if (serial !== this.seriesNumber) {
            return;
          }
          this.hostNames = [];
          this.hostNameIndex = 0;
        });
    },
    showPreviousHostName() {
      this.hostNameIndex =
        (this.hostNameIndex - 1 + this.hostNames.length) % this.hostNames.length;
    },
    showNextHostName() {
      this.hostNameIndex = (this.hostNameIndex + 1) % this.hostNames.length;
    }
  }
};
</script>

<style lang="scss" scoped>
.update-box {
  position: absolute;
  top: 506px;
  left: 379px;
  display: flex;
  flex-wrap: nowrap;
  width: 77px;
  height: 24px;
  padding: 0;
  align-items: center;
  border: 0;
  background: transparent;
  color: rgba(67, 142, 255, 0.8);
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 400;
  line-height: 24px;
  white-space: nowrap;
}

.update-icon {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  background: url("/images/dashboard/update.png") no-repeat center / 100% 100%;
}

.update-box span:last-child {
  flex: 0 0 auto;
  white-space: nowrap;
}

.update-box:disabled {
  cursor: not-allowed;
  opacity: 0.75;
}

.update-box:disabled .update-icon {
  animation: refresh-icon-rotate 1s linear infinite;
}

.refresh-loading {
  position: absolute;
  z-index: 50;
  top: 494px;
  bottom: 0;
  left: 29px;
  display: flex;
  width: 440px;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: rgba(2, 16, 31, 0.88);
  color: #fff;
}

.refresh-loading__spinner {
  position: relative;
  display: flex;
  width: 88px;
  height: 88px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(15, 49, 83, 0.9);
  box-shadow: 0 0 24px rgba(63, 175, 255, 0.35);
  color: #3fafff;
  font-size: 22px;
  font-weight: 600;
}

.refresh-loading__spinner::before {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border: 4px solid rgba(63, 175, 255, 0.2);
  border-top-color: #3fafff;
  border-right-color: #438eff;
  border-radius: 50%;
  content: "";
  animation: refresh-loading-rotate 1s linear infinite;
}

.refresh-loading p {
  margin: 18px 0 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  line-height: 24px;
}

.refresh-loading-fade-enter-active,
.refresh-loading-fade-leave-active {
  transition: opacity 0.2s ease;
}

.refresh-loading-fade-enter,
.refresh-loading-fade-leave-to {
  opacity: 0;
}

@keyframes refresh-loading-rotate {
  to { transform: rotate(360deg); }
}

@keyframes refresh-icon-rotate {
  to { transform: rotate(360deg); }
}

.device-image {
  position: absolute;
  top: 530px;
  left: 48px;
  width: 400px;
  height: 236px;
  background: url("/images/dashboard/shebeitu.png") no-repeat center / 100% 100%;
}

.device-info-list {
  position: absolute;
  top: 806px;
  left: 52px;
  width: 396px;
  margin: 0;
  overflow: visible;
  white-space: nowrap;
}

.device-info-row {
  display: flex;
  height: 38px;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 18px;
  white-space: nowrap;
}

.device-info-row dt {
  display: flex;
  flex: 0 0 132px;
  align-items: center;
  box-sizing: border-box;
  font-weight: 400;
  white-space: nowrap;
}

.device-info-row dt::before {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  margin-right: 6px;
  background: url("/images/dashboard/shebeicanshuicon.png") no-repeat center / 100% 100%;
  content: "";
}

.device-info-row dd {
  flex: 0 0 auto;
  margin: 0 0 0 20px;
  color: #3fafff;
  font-weight: 500;
  white-space: nowrap;
}

.device-info-row dd.is-online { color: #049344; }
.device-info-row dd.is-offline { color: #ff0000; }

.device-name-row dd {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.host-name-switcher {
  display: flex;
  flex: 0 0 20px;
  width: 20px;
  margin-left: 8px;
  align-items: center;
  flex-direction: column;
  justify-content: center;
}

.host-name-switcher__icon {
  display: flex;
  width: 20px;
  height: 16px;
  align-items: center;
  justify-content: center;
  color: rgba(63, 175, 255, 0.9);
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}

.host-name-switcher__icon:hover,
.host-name-switcher__icon:focus {
  color: #fff;
  outline: none;
}
</style>
